import { useCallback, useEffect, useMemo } from "react";

// Algum áudio/vídeo da página (player de mensagem, gravação, chamada) tocando
// agora. O elemento da notificação não está no DOM, então não entra na conta.
const isPageMediaPlaying = () => {
	try {
		return Array.from(document.querySelectorAll("audio, video")).some(
			media => !media.paused && !media.ended && !media.muted && media.readyState > 2
		);
	} catch (err) {
		return false;
	}
};

const clampVolume = value => {
	const parsed = Number(value);
	if (!Number.isFinite(parsed)) return 1;
	return Math.min(Math.max(parsed, 0), 1);
};

/**
 * Toca um som curto de notificação usando um elemento <audio> puro.
 *
 * Substitui o use-sound/Howler de propósito. O Howler tem dois modos e os dois
 * falham aqui:
 *
 *  - Web Audio (padrão): suspende o AudioContext após 30s de silêncio e depende
 *    de timers para retomá-lo — e o navegador estrangula timers exatamente em
 *    abas de segundo plano, que é quando a notificação importa.
 *  - html5: true: espera o evento `canplaythrough` antes de tocar. O Firefox não
 *    dispara esse evento de forma confiável (depende do encoding do arquivo), e
 *    o Howler fica preso em `_playLock` para sempre, em silêncio.
 *
 * Um <audio> direto não tem nenhum dos dois problemas, e ainda marca a aba como
 * "audível", o que a isenta do throttling de segundo plano.
 */
const useNotificationSound = (src, volume) => {
	const audio = useMemo(() => {
		const element = new Audio(src);
		element.preload = "auto";
		return element;
	}, [src]);

	const level = clampVolume(volume);

	useEffect(() => {
		audio.volume = level;
	}, [audio, level]);

	return useCallback(() => {
		if (level === 0) return;

		// Atendente ouvindo um áudio do cliente: o bipe por cima atrapalhava (e
		// no navegador chegava a interromper) o player. O aviso visual — badge,
		// notificação do sistema, título da aba — continua; só o som é poupado.
		if (isPageMediaPlaying()) return;

		// Rebobina para que mensagens em sequência rápida toquem de novo em vez de
		// serem engolidas por uma reprodução ainda em andamento.
		try {
			audio.currentTime = 0;
		} catch (err) {
			// currentTime lança se a mídia ainda não tem metadados; play() resolve isso.
		}

		const attempt = audio.play();
		if (attempt && typeof attempt.catch === "function") {
			attempt.catch(err => {
				console.warn(`[notificação] falha ao tocar ${src}: ${err.name}`);
			});
		}
	}, [audio, level, src]);
};

export default useNotificationSound;
